import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LoadingState } from 'src/app/core/constants/observable-state.interface';
import { ProductCollection } from 'src/app/core/interfaces/store-configuration.interface';
import { ListResponse } from 'src/app/core/models/list-response.model';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { MappedProductListingSearch } from '../models/product-listing.model';
import { MappedProductSearch, ProductSearch } from '../models/product-search.model';
import { Assets, CategoryDetail, Collection, Downloads, Feature, Product, ProductStateManager } from '../models/product.model';
import { AvailableBid, ProductsApiService, QuotedBid } from './products-api.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsRepositoryService {
  private productsApiService = inject(ProductsApiService);
  private environmentService = inject(EnvironmentService);

  // Product state manager for detailed product data
  private productStateManager = new ProductStateManager();

  // ===== SERVICE STATE MANAGEMENT =====
  private _serviceError = signal<string | null>(null);
  private _isLoading = signal<boolean>(false);
  private _isLoadingCollections = signal<boolean>(false);

  // ===== DATA STATE SIGNALS =====
  private _category = signal<CategoryDetail | null>(null);
  private _products = signal<MappedProductSearch | null>(null);
  private _productsV2 = signal<MappedProductListingSearch | null>(null);
  private _productsByIds = signal<ProductSearch | null>(null);
  private _productsForCarousel = signal<ProductSearch | null>(null);
  private _product = signal<Product | null>(null);
  private _productsByScrollId = signal<ProductSearch | null>(null);
  private _collection = signal<Collection | null>(null);

  // ===== PENDING REQUESTS FOR ENVIRONMENT READINESS =====
  private _pendingProductId = signal<string | null>(null);
  private _pendingMakeNewApiCall = signal<boolean>(false);

  // ===== PUBLIC READONLY SIGNALS =====
  readonly serviceError = this._serviceError.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isLoadingCollections = this._isLoadingCollections.asReadonly();

  // Data signals
  readonly category = this._category.asReadonly();
  readonly products = this._products.asReadonly();
  readonly productsV2 = this._productsV2.asReadonly();
  readonly productsByIds = this._productsByIds.asReadonly();
  readonly productsForCarousel = this._productsForCarousel.asReadonly();
  readonly product = this._product.asReadonly();
  readonly productsByScrollId = this._productsByScrollId.asReadonly();
  readonly collection = this._collection.asReadonly();

  // ===== COMPUTED SIGNALS =====

  // Environment readiness check
  readonly isEnvironmentReady = computed(() => this.environmentService.isInitialized());

  readonly hasCategory = computed(() => !!this._category());
  readonly categoryName = computed(() => this._category()?.name);
  readonly categoryUrlPath = computed(() => this._category()?.url_path);

  readonly hasProducts = computed(() => !!this._products());
  readonly hasProductsV2 = computed(() => !!this._productsV2());
  readonly hasProductsByIds = computed(() => !!this._productsByIds());
  readonly hasProductsForCarousel = computed(() => !!this._productsForCarousel());
  readonly hasProduct = computed(() => !!this._product());
  readonly hasProductsByScrollId = computed(() => !!this._productsByScrollId());
  readonly hasCollection = computed(() => !!this._collection());

  // Enhanced computed signals
  readonly isLoadingProducts = computed(() => this._isLoading());
  readonly isLoadingProduct = computed(() => this._isLoading());
  readonly isLoadingCategory = computed(() => this._isLoading());
  readonly hasError = computed(() => !!this._serviceError());
  readonly currentError = computed(() => this._serviceError());

  cachedProductCollectiontResponse!: ListResponse<ProductCollection>;
  private cachedCollectionResponse: { [key: string]: Collection } = {};

  // Store pending params for reactive retry when environment becomes ready
  private _pendingProductsV2Params = signal<any>(null);
  readonly pendingProductsV2Params = this._pendingProductsV2Params.asReadonly();

  // ===== ANGULAR 20 SERVICE AUTO-INITIALIZATION =====

  constructor() {
    // Reactive effect: automatically retry products loading when environment becomes ready
    effect(() => {
      const isReady = this.isEnvironmentReady();
      const pendingParams = this._pendingProductsV2Params();
      const pendingProductId = this._pendingProductId();
      const pendingMakeNewApiCall = this._pendingMakeNewApiCall();

      if (isReady && pendingParams) {
        this._pendingProductsV2Params.set(null); // Clear pending params
        this.getProductsV2(pendingParams); // Retry the API call
      }

      if (isReady && pendingProductId) {
        this._pendingProductId.set(null); // Clear pending product ID
        this._pendingMakeNewApiCall.set(false); // Clear pending flag
        this.getProduct(pendingProductId, pendingMakeNewApiCall); // Retry the API call
      }
    });
  }

  // ===== UTILITY METHODS =====
  resetService(): void {
    this._serviceError.set(null);
    this._isLoading.set(false);
    this.clearAllData();
  }

  getServiceStatus(): {
    hasErrors: boolean;
    isLoading: boolean;
    error: string | null;
  } {
    return {
      hasErrors: !!this._serviceError(),
      isLoading: this._isLoading(),
      error: this._serviceError()
    };
  }

  // ===== SIGNAL-BASED METHODS =====
  async getProducts(params: any): Promise<void> {

    // Don't block if already loading - allow concurrent operations
    this._isLoading.set(true);
    this._serviceError.set(null);

    try {
      const products = await firstValueFrom(this.productsApiService.getProducts(params));
      this._products.set(products);
    } catch (error) {
      // Failed to get products
      this._products.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load products');
    } finally {
      // Always reset loading state
      this._isLoading.set(false);
    }
  }

  async getProductsV2(params: any): Promise<void> {
    // Check if environment is ready - Angular 20 reactive approach
    if (!this.isEnvironmentReady()) {
      // Store params for reactive retry when environment becomes ready
      this._pendingProductsV2Params.set(params);
      return;
    }


    // Don't block if already loading - allow concurrent operations
    this._isLoading.set(true);
    this._serviceError.set(null);

    try {
      const products = await firstValueFrom(this.productsApiService.getProductsForVersion2(params));
      this._productsV2.set(products);
    } catch (error) {
      // Failed to get products V2
      this._productsV2.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load products V2');
    } finally {
      // Always reset loading state
      this._isLoading.set(false);
    }
  }

  async getProductsByIds(productIds: string[]): Promise<void> {

    try {
      const products = await firstValueFrom(this.productsApiService.getProductsByIds(productIds));
      this._productsByIds.set(products);
    } catch (error) {
      // Failed to get products by IDs
      this._productsByIds.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load products by IDs');
    }
  }

  async getProductsForCarousel(params: any): Promise<void> {

    try {
      const products = await firstValueFrom(this.productsApiService.getProductsForCarousel(params));
      this._productsForCarousel.set(products);
    } catch (error) {
      // Failed to get products for carousel
      this._productsForCarousel.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load carousel products');
    }
  }

  async getProduct(productId: string, makeNewApiCall: boolean): Promise<void> {
    // Check if environment is ready before making API call
    if (!this.isEnvironmentReady()) {
      // Store the product ID and parameters for retry when environment is ready
      this._pendingProductId.set(productId);
      this._pendingMakeNewApiCall.set(makeNewApiCall);
      return;
    }

    this._isLoading.set(true);
    this._serviceError.set(null);

    try {
      this.productStateManager.setLoading(true);
      const product = await firstValueFrom(this.productsApiService.getProduct(productId, makeNewApiCall));

      // Set the main product data
      this._product.set(product);
      this.productStateManager.setProduct(product);

      // Handle additional product data (features, downloads, assets, etc.)
      if (product) {
        // Load additional data using the URLs from product properties
        await this.loadProductFeatures(product);
        await this.loadProductDownloads(product);
        await this.loadProductAssets(product);
        await this.loadRelatedProducts(product);
      }

      this.productStateManager.setLoading(false);
      this.productStateManager.setError(null);
      this._isLoading.set(false);
    } catch (error) {
      // Failed to get product
      this._product.set(null);
      this.productStateManager.setLoading(false);
      this.productStateManager.setError(error instanceof Error ? error.message : 'Failed to load product');
      this._isLoading.set(false);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load product');
    }
  }

  private async loadProductFeatures(product: Product): Promise<void> {
    if (product.features) {
      try {
        // Set loading state
        this.productStateManager.setFeaturesLoading(true);

        const featuresState = await firstValueFrom(this.productsApiService.createGetRequestFromUrl<{ features: Feature[] }>(product.features)!);

        if (featuresState && featuresState.response) {
          this.productStateManager.setFeatures(featuresState.response.features);
        }
      } catch (error) {
        this.productStateManager.setFeaturesError(error instanceof Error ? error.message : 'Failed to load features');
      }
    }
  }

  private async loadProductDownloads(product: Product): Promise<void> {
    if (product.downloads) {
      try {
        // Set loading state
        this.productStateManager.setDownloadsLoading(true);

        const downloadsState = await firstValueFrom(this.productsApiService.createGetRequestFromUrl<{ downloads: Downloads }>(product.downloads)!);

        if (downloadsState && downloadsState.response) {
          this.productStateManager.setDownloads(downloadsState.response.downloads);
        }
      } catch (error) {
        this.productStateManager.setDownloadsError(error instanceof Error ? error.message : 'Failed to load downloads');
      }
    }
  }

  private async loadProductAssets(product: Product): Promise<void> {
    if (product.assets) {
      try {
        // Set loading state
        this.productStateManager.setAssetsLoading(true);

        const assetsState = await firstValueFrom(this.productsApiService.createGetRequestFromUrl<{ assets: Assets }>(product.assets)!);

        if (assetsState && assetsState.response) {
          this.productStateManager.setAssets(assetsState.response.assets);
        }
      } catch (error) {
        this.productStateManager.setAssetsError(error instanceof Error ? error.message : 'Failed to load assets');
      }
    }
  }

  private async loadRelatedProducts(product: Product): Promise<void> {
    // Load related products
    const relatedProducts = [
      { url: (product.similar_products as any)?.url, count: (product.similar_products as any)?.count || 0, type: 'similar' },
      { url: product.frequently_bought_together_products?.url, count: product.frequently_bought_together_products?.count || 0, type: 'frequently' },
      { url: (product.sponsored_products as any)?.url, count: (product.sponsored_products as any)?.count || 0, type: 'sponsored' },
      { url: (product.accessories as any)?.url, count: (product.accessories as any)?.count || 0, type: 'accessories' }
    ];

    for (const item of relatedProducts) {
      if (item.url && item.count > 0) {
        // Set loading state
        switch (item.type) {
          case 'similar':
            this.productStateManager.setSimilarProductsLoading(true);
            break;
          case 'frequently':
            this.productStateManager.setFrequentlyBoughtTogetherLoading(true);
            break;
          case 'sponsored':
            this.productStateManager.setSponsoredProductsLoading(true);
            break;
          case 'accessories':
            this.productStateManager.setAccessoriesLoading(true);
            break;
        }

        try {
          // Filter out loading state and wait for actual data
          const productsState = await firstValueFrom(
            this.productsApiService.createGetRequestFromUrlForProductsList<Product[]>({
              url: item.url,
              count: item.count
            })!.pipe(
              filter(state => state.loadingState !== LoadingState.Loading)
            )
          );

          if (productsState && productsState.response) {
            switch (item.type) {
              case 'similar':
                this.productStateManager.setSimilarProducts(productsState.response);
                break;
              case 'frequently':
                this.productStateManager.setFrequentlyBoughtTogether(productsState.response);
                break;
              case 'sponsored':
                this.productStateManager.setSponsoredProducts(productsState.response);
                break;
              case 'accessories':
                this.productStateManager.setAccessories(productsState.response);
                break;
            }
          }
        } catch (error) {
          // Set error state
          const errorMessage = error instanceof Error ? error.message : `Failed to load ${item.type} products`;
          switch (item.type) {
            case 'similar':
              this.productStateManager.setSimilarProductsError(errorMessage);
              break;
            case 'frequently':
              this.productStateManager.setFrequentlyBoughtTogetherError(errorMessage);
              break;
            case 'sponsored':
              this.productStateManager.setSponsoredProductsError(errorMessage);
              break;
            case 'accessories':
              this.productStateManager.setAccessoriesError(errorMessage);
              break;
          }
        }
      } else {
      }
    }

    // Load products in box
    if (product.products_in_box) {
      try {
        // Set loading state
        this.productStateManager.setProductsInABoxLoading(true);

        const observable = this.productsApiService.createGetRequestFromUrl<{ products_in_box: string[] }>(product.products_in_box);

        if (observable) {
          const productsInBoxState = await firstValueFrom(observable);

          if (productsInBoxState && productsInBoxState.response) {
            this.productStateManager.setProductsInABox(productsInBoxState.response.products_in_box);
          }
        }

        // CRITICAL FIX: Always reset loading state
        this.productStateManager.setProductsInABoxLoading(false);

      } catch (error) {
        this.productStateManager.setProductsInABoxError(error instanceof Error ? error.message : 'Failed to load products in box');

        // CRITICAL FIX: Reset loading state on error too
        this.productStateManager.setProductsInABoxLoading(false);
      }
    }
  }

  async getProductsByScrollId(scroll_id: string): Promise<void> {

    try {
      const products = await firstValueFrom(this.productsApiService.getProductsByScrollId(scroll_id));
      this._productsByScrollId.set(products);
    } catch (error) {
      // Failed to get products by scroll ID
      this._productsByScrollId.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load products by scroll ID');
    }
  }

  async getProductCategory(category_url_path: string): Promise<void> {

    try {
      const category = await firstValueFrom(this.productsApiService.getProductCategory(category_url_path));
      this._category.set(category);
    } catch (error) {
      // Failed to get product category
      this._category.set(null);
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to load category');
    }
  }

  async sendCustomerBid(productId: string, biddingDetails: QuotedBid): Promise<AvailableBid | null> {

    try {
      const response = await firstValueFrom(this.productsApiService.sendCustomerBid(productId, biddingDetails));
      return response;
    } catch (error) {
      // Failed to send customer bid
      this._serviceError.set(error instanceof Error ? error.message : 'Failed to send customer bid');
      return null;
    }
  }

  async getCollectionDetails(collectionId: string): Promise<void> {

    if (this.cachedCollectionResponse[collectionId]) {
      this._collection.set(this.cachedCollectionResponse[collectionId]!);
    } else {
      try {
        // Set collection loading state (independent of other operations)
        this._isLoadingCollections.set(true);

        const collection = await firstValueFrom(this.productsApiService.getCollectionDetails(collectionId));
        this.cachedCollectionResponse[collectionId] = collection;
        this._collection.set(collection);
      } catch (error) {
        // Failed to get collection details
        this._collection.set(null);
        this._serviceError.set(error instanceof Error ? error.message : 'Failed to load collection details');
      } finally {
        // Clear collection loading state
        this._isLoadingCollections.set(false);
      }
    }
  }

  // Get ProductStateManager for accessing detailed product state
  getProductStateManager(): ProductStateManager {
    return this.productStateManager;
  }

  // Public setter methods for signals
  setCategory(category: CategoryDetail | null) {
    this._category.set(category);
  }

  setProducts(products: MappedProductSearch | null) {
    this._products.set(products);
  }

  setProduct(product: Product | null) {
    this._product.set(product);
  }

  setCollection(collection: Collection | null) {
    this._collection.set(collection);
  }

  // Clear methods
  clearProducts() {
    this._products.set(null);
  }

  clearProduct() {
    this._product.set(null);
  }

  clearCategory() {
    this._category.set(null);
  }

  clearCollection() {
    this._collection.set(null);
  }

  // Utility methods for better signal management
  clearAllData() {
    this.clearProducts();
    this.clearProduct();
    this.clearCategory();
    this.clearCollection();
    this._serviceError.set(null);
    this._isLoading.set(false);
  }

  clearError() {
    this._serviceError.set(null);
  }

  // Reactive state management methods
  getCurrentState() {
    return {
      isLoading: this._isLoading(),
      error: this._serviceError(),
      hasData: this.hasCategory() || this.hasProducts() || this.hasProduct() || this.hasCollection()
    };
  }

  // Enhanced error handling
  setError(error: string) {
    this._serviceError.set(error);
    this._isLoading.set(false);
  }

  clearLoading() {
    this._isLoading.set(false);
  }
}
